import React from "react";
import { useRemoteOptions } from "../../hooks/useRemoteOptions.js";
import type {
  ComboboxOption,
  ComboboxPage,
  ComboboxProps,
} from "./Combobox.types.js";
import {
  remoteListboxRows,
  type ComboboxListboxRows,
} from "./combobox-rows.js";

/* The Combobox's text and rows: what the input says and what the open
   listbox lists. `options` filters on the client; `loadOptions` instead loads
   cursor pages while the listbox is open, on the request engine
   FilterOptionList shares (useRemoteOptions). Split out of Combobox.tsx so
   the async path did not grow that file. Internal: not in the barrel. */

const NO_REMOTE_LOAD = (): Promise<ComboboxPage> =>
  Promise.resolve({ options: [], nextCursor: null });

/** The props that decide where the rows come from. */
export type ComboboxRowsSource = Pick<
  ComboboxProps,
  | "options"
  | "loadOptions"
  | "filter"
  | "minQueryLength"
  | "debounceMs"
  | "valueLabel"
>;

export type ComboboxRowsInput = ComboboxRowsSource & {
  readonly emptyMessage: string;
  /** The selected value. */
  readonly current: string | undefined;
  /** Whether the listbox is logically open. */
  readonly open: boolean;
};

export function useComboboxRows({
  options,
  loadOptions,
  filter,
  minQueryLength,
  debounceMs,
  valueLabel,
  emptyMessage,
  current,
  open,
}: ComboboxRowsInput) {
  const remoteMode = loadOptions !== undefined;
  // Async only: what the user typed since opening ("" = the first page).
  const [search, setSearch] = React.useState("");
  // Async only: the last pick, so its label outlives the page it came from.
  const [picked, setPicked] = React.useState<ComboboxOption | null>(null);
  const remote = useRemoteOptions<ComboboxOption>({
    load: loadOptions ?? NO_REMOTE_LOAD,
    query: search,
    enabled: remoteMode && open,
    debounceMs,
    minQueryLength,
  });

  const known = remoteMode ? remote.options : (options ?? []);
  const selectedLabel =
    known.find((o) => o.value === current)?.label ??
    (remoteMode && current
      ? picked?.value === current
        ? picked.label
        : valueLabel
      : undefined) ??
    "";

  const [query, setQuery] = React.useState(selectedLabel);

  // Keep the input text in sync when the selection changes from outside.
  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: after a close has committed, the text snaps to whatever the selection now is — a controlled consumer that refused a pick or a clear gets its own label back.
    if (!open) setQuery(selectedLabel);
  }, [selectedLabel, open]);

  const q = query.trim().toLowerCase();
  const showAll = q === "" || (selectedLabel && query === selectedLabel);
  const rows: ComboboxListboxRows = remoteMode
    ? remoteListboxRows(remote, emptyMessage)
    : {
        options: showAll
          ? known
          : known.filter((o) =>
              filter ? filter(o, query) : o.label.toLowerCase().includes(q),
            ),
      };

  return {
    remoteMode,
    rows,
    selectedLabel,
    query,
    setQuery,
    setSearch,
    setPicked,
  };
}
